import React, { useEffect, useState } from "react";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useNavigate } from "react-router-dom";
import { Hub } from "aws-amplify/utils";
import { fetchAuthSession, signInWithRedirect, signIn } from "aws-amplify/auth";
import { Amplify } from "aws-amplify";

const OAuthCallback: React.FC = () => {
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [hasCode, setHasCode] = useState(false);
  const { authStatus, user } = useAuthenticator();
  const navigate = useNavigate();

  useEffect(() => {
    const handleOAuthCallback = async () => {
      console.log("=== OAuth Callback Debug ===");
      console.log("Auth status:", authStatus);
      console.log("User:", user);

      // Check for OAuth errors
      const urlParams = new URLSearchParams(window.location.search);
      const error = urlParams.get("error");
      const errorDescription = urlParams.get("error_description");

      if (error) {
        setError(`Authentication error: ${errorDescription || error}`);
        setIsProcessing(false);
        return;
      }

      // If we're already authenticated, redirect to dashboard
      if (authStatus === "authenticated" && user) {
        console.log("User is already authenticated, redirecting to dashboard");
        setIsSuccess(true);
        setIsProcessing(false);
        setTimeout(() => {
          navigate("/dashboard", { replace: true });
        }, 1000);
        return;
      }

      // If we're still configuring, wait
      if (authStatus === "configuring") {
        console.log("Auth is still configuring, waiting...");
        return;
      }

      // If we're unauthenticated and have a code, manually exchange it
      if (authStatus === "unauthenticated") {
        const code = urlParams.get("code");
        if (code) {
          console.log("OAuth code received, manually exchanging for tokens...");
          setHasCode(true);

          try {
            // Get configuration values
            const cognitoDomain =
              process.env.REACT_APP_COGNITO_DOMAIN ||
              "standardleagueauth-standardleagueauth.auth.us-east-2.amazoncognito.com";
            const clientId =
              process.env.REACT_APP_USER_POOL_CLIENT_ID ||
              "577808791340-1qpuubpjnos8tsjlabckblfloga215jr.apps.googleusercontent.com";
            const redirectUri =
              process.env.REACT_APP_REDIRECT_SIGN_IN ||
              "http://localhost:3000/oauth/callback";

            // Exchange code for tokens
            const tokenResponse = await fetch(
              `https://${cognitoDomain}/oauth2/token`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/x-www-form-urlencoded",
                },
                body: new URLSearchParams({
                  grant_type: "authorization_code",
                  client_id: clientId,
                  code: code,
                  redirect_uri: redirectUri,
                }),
              }
            );

            console.log("Token response status:", tokenResponse.status);

            if (tokenResponse.ok) {
              const tokenData = await tokenResponse.json();
              console.log("Token exchange successful:", tokenData);

              // Store tokens in sessionStorage
              sessionStorage.setItem("access_token", tokenData.access_token);
              sessionStorage.setItem("id_token", tokenData.id_token);
              sessionStorage.setItem("refresh_token", tokenData.refresh_token);

              // Extract and store email from ID token
              try {
                const payload = JSON.parse(
                  atob(tokenData.id_token.split(".")[1])
                );
                sessionStorage.setItem("email", payload.email);
                console.log("OAuthCallback: Stored email:", payload.email);
              } catch (error) {
                console.error("Error extracting email from token:", error);
              }

              console.log("Tokens stored, redirecting to dashboard");
              setIsSuccess(true);
              setIsProcessing(false);

              // Redirect to dashboard
              setTimeout(() => {
                navigate("/dashboard", { replace: true });
              }, 1000);
            } else {
              const errorData = await tokenResponse.text();
              console.error("Token exchange failed:", errorData);
              setError("Token exchange failed");
              setIsProcessing(false);
            }
          } catch (error) {
            console.error("Error during token exchange:", error);
            setError("Authentication failed");
            setIsProcessing(false);
          }
        } else {
          console.log("No OAuth code found, redirecting to home");
          setError("No authentication code received");
          setIsProcessing(false);
        }
      }
    };

    handleOAuthCallback();
  }, [authStatus, user, navigate]);

  // Show loading while processing
  if (isProcessing && (authStatus === "configuring" || hasCode)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-lg">
            {hasCode ? "Processing authentication..." : "Completing sign in..."}
          </p>
          <p className="text-sm text-gray-500 mt-2">
            {hasCode ? "Please wait while we complete your sign in..." : ""}
          </p>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-green-500 mb-4">
            <svg
              className="w-12 h-12 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold mb-2 text-green-600">
            Welcome!
          </h2>
          <p className="text-gray-600 mb-2">
            Successfully signed in as{" "}
            <span className="font-semibold">
              {user?.signInDetails?.loginId || user?.username || "User"}
            </span>
          </p>
          <p className="text-sm text-gray-500 mb-4">
            Redirecting to dashboard...
          </p>
          <div className="animate-pulse">
            <div className="w-2 h-2 bg-green-500 rounded-full mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <svg
              className="w-12 h-12 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold mb-2">Authentication Failed</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate("/", { replace: true })}
            className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default OAuthCallback;
